import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { PicklistOutcome } from '../types';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function formatDateShort(date: string | Date | null | undefined): string {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy', { locale: fr });
}

export function formatDateTime(date: string | Date | null | undefined): string {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: fr });
}

const currencyFormatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
});

export function formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return currencyFormatter.format(value);
}

// Palette cyclique pour les statuts "ouverts" (dont la liste est libre, gérée
// par l'admin) — l'issue (gagné/perdu/sans suite) prime toujours sur la couleur
// cyclique pour rester immédiatement lisible.
const OPEN_PALETTE = [
    { bg: '#e0f2fe', fg: '#0369a1' },
    { bg: '#fef3c7', fg: '#b45309' },
    { bg: '#f3e8ff', fg: '#7c3aed' },
    { bg: '#e0e7ff', fg: '#4338ca' },
    { bg: '#f1f5f9', fg: '#475569' },
    { bg: '#fae8ff', fg: '#a21caf' },
];

const OUTCOME_STYLE: Record<Exclude<PicklistOutcome, 'open'>, { bg: string; fg: string }> = {
    won: { bg: '#dcfce7', fg: '#16a34a' },
    lost: { bg: '#fee2e2', fg: '#dc2626' },
    no_decision: { bg: '#f1f5f9', fg: '#94a3b8' },
};

function hashString(value: string): number {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = (hash * 31 + value.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
}

// `color` : couleur choisie par l'admin pour ce statut (picklists.color) —
// prioritaire sur la logique automatique (issue fixe / palette cyclique)
// quand elle est définie.
export function getStatusBadgeStyle(
    statusValue: string,
    outcome: PicklistOutcome,
    color?: string | null
): { backgroundColor: string; color: string } {
    if (color) {
        return { backgroundColor: `${color}26`, color };
    }
    if (outcome !== 'open') {
        const style = OUTCOME_STYLE[outcome];
        return { backgroundColor: style.bg, color: style.fg };
    }
    const palette = OPEN_PALETTE[hashString(statusValue) % OPEN_PALETTE.length];
    return { backgroundColor: palette.bg, color: palette.fg };
}
